import React, { useState } from 'react';
import './VerificationPortal.css';

function VerificationPortal({ onBack, onApprove, analysisData }) {
  const [verificationNotes, setVerificationNotes] = useState('');
  const [checklist, setChecklist] = useState({
    gpsVerified: false,
    satelliteDataVerified: false,
    qualityChecked: false,
    coordinatesValid: false
  });

  const submissionDetails = {
    submissionId: 'MANG-3802',
    date: '15/10/2023',
    location: 'New Delhi, India',
    dataQuality: 'High',
    status: 'High'
  };

  const handleChecklistChange = (key) => {
    setChecklist(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleApprove = () => {
    const allChecked = Object.values(checklist).every(val => val === true);
    
    if (!allChecked) {
      alert('Please complete all verification checklist items before approving.');
      return;
    }

    if (!verificationNotes.trim()) {
      alert('Please add verification notes before approving.');
      return;
    }

    console.log('Approved with notes:', verificationNotes);
    console.log('Checklist:', checklist);
    
    // Navigate to third-party verification
    if (onApprove) {
      onApprove();
    }
  };

  const handleReject = () => {
    if (!verificationNotes.trim()) {
      alert('Please add verification notes explaining the reason for rejection.');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to reject this submission?');
    if (confirmed) {
      alert('❌ Submission Rejected. The submitter will be notified.');
      console.log('Rejected with notes:', verificationNotes);
    }
  };

  const allChecklistComplete = Object.values(checklist).every(val => val === true);
  const completionPercentage = Math.round(
    (Object.values(checklist).filter(val => val === true).length / Object.values(checklist).length) * 100
  );

  return (
    <div className="verification-portal">
      {/* Header */}
      <header className="verification-header">
        <div className="header-content">
          <h1 className="verification-title">Verification Portal</h1>
          <p className="verification-subtitle">Expert validation and quality assurance</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="verification-container">
        {/* Submission Details Section */}
        <section className="verification-section">
          <div className="section-header">
            <span className="section-icon">📋</span>
            <span className="section-title">Submission Details</span>
          </div>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Submission ID:</span>
              <span className="detail-value">{submissionDetails.submissionId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Date:</span>
              <span className="detail-value">{submissionDetails.date}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Location:</span>
              <span className="detail-value">{submissionDetails.location}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Data Quality:</span>
              <span className="detail-value status-high">{submissionDetails.dataQuality}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status:</span>
              <span className="detail-value status-badge">{submissionDetails.status}</span>
            </div>
          </div>
        </section>

        {/* Analysis Summary Section */}
        <section className="verification-section">
          <div className="section-header">
            <span className="section-icon">📊</span>
            <span className="section-title">Analysis Summary</span>
          </div>
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-value">2.3</div>
              <div className="summary-label">tons CO₂</div>
            </div>
            <div className="summary-card">
              <div className="summary-value">1.2</div>
              <div className="summary-label">hectares</div>
            </div>
          </div>
          <div className="summary-details">
            <div className="summary-row">
              <span className="summary-label-text">Vegetation Coverage</span>
              <span className="summary-value-text">74%</span>
            </div>
            <div className="summary-row">
              <span className="summary-label-text">AI Confidence</span>
              <span className="summary-value-text">94%</span>
            </div>
          </div>
        </section>

        {/* Verification Checklist Section */}
        <section className="verification-section">
          <div className="section-header">
            <span className="section-icon">✓</span>
            <span className="section-title">Verification Checklist</span>
            <span className="completion-badge">{completionPercentage}%</span>
          </div>
          <div className="checklist-items">
            <label className="checklist-item">
              <input
                type="checkbox"
                checked={checklist.gpsVerified}
                onChange={() => handleChecklistChange('gpsVerified')}
              />
              <span className="checklist-text">GPS coordinates verified</span>
            </label>
            <label className="checklist-item">
              <input
                type="checkbox"
                checked={checklist.satelliteDataVerified}
                onChange={() => handleChecklistChange('satelliteDataVerified')}
              />
              <span className="checklist-text">Satellite data cross-referenced</span>
            </label>
            <label className="checklist-item">
              <input
                type="checkbox"
                checked={checklist.qualityChecked}
                onChange={() => handleChecklistChange('qualityChecked')}
              />
              <span className="checklist-text">Image quality meets standards</span>
            </label>
            <label className="checklist-item">
              <input
                type="checkbox"
                checked={checklist.coordinatesValid}
                onChange={() => handleChecklistChange('coordinatesValid')}
              />
              <span className="checklist-text">Coordinates within valid range</span>
            </label>
          </div>
        </section>

        {/* Verification Notes Section */}
        <section className="verification-section">
          <div className="section-header">
            <span className="section-icon">📝</span>
            <span className="section-title">Verification Notes</span>
          </div>
          <div className="notes-content">
            <textarea
              className="notes-textarea"
              placeholder="Add any comments or observations..."
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              rows={5}
            />
          </div>
        </section>

        {/* Action Buttons */}
        <div className="action-section">
          <button 
            className="action-btn approve-btn"
            onClick={handleApprove}
            disabled={!allChecklistComplete}
          >
            <span className="btn-icon">✓</span>
            Approve and Register on Blockchain
          </button>
          <button 
            className="action-btn reject-btn"
            onClick={handleReject}
          >
            <span className="btn-icon">✗</span>
            Reject Submission
          </button>
        </div>

        {/* Footer Note */}
        <div className="footer-note">
          <p>Complete all verification checks before approving submission</p>
        </div>
      </div>
    </div>
  );
}

export default VerificationPortal;
